import * as React from 'react';
import {
    InvoiceList,
    InvoiceProvisionPayment,
    InvoiceStatus,
    NoResult,
    useCollectionBillingCtx,
    useCurrentDossierCtx,
    useBillingRender,
    useBillingActions,
    useModalMail,
    DossierPrescriberContext,
    useTransition,
    useCurrentDossierCtxDispatch,
    REFRESH_CURRENT_DOSSIER_CTX,
} from '@europrocurement/l2d-modules';
import { Box, Card, PaletteColor, Typography, useTheme } from '@mui/material';
import { Switch } from '@europrocurement/flexy-components/components/atoms';
import { Accordion, useModal } from '@europrocurement/flexy-components';
import { enqueueSnackbar } from 'notistack';
import usePayActions from '@europrocurement/l2d-modules/modules/Dossiers/components/detail/DossierFacturation/hooks/usePayActions';
import {
    REALM_INTERN,
    UseKeycloakCheckRole,
    UseKeycloakService,
} from '@europrocurement/l2d-keycloak';

type QuoteAndBillsSectionProps = {
    collectionIds: number[];
};

const QuoteAndBillsSection: React.FunctionComponent<QuoteAndBillsSectionProps> = function ({
    collectionIds,
}) {
    const { dossier, siblings } = useCurrentDossierCtx();
    const dispatch = useCurrentDossierCtxDispatch();

    const { palette } = useTheme();
    const {
        getCollectionInvoices,
        getMultipleCollectionEstimateFiltered,
        getRecipientId,
        getCollectionById,
    } = useCollectionBillingCtx();

    const [showInactive, setShowInactive] = React.useState<boolean>(false);
    const invoices = getCollectionInvoices();
    const estimates = getMultipleCollectionEstimateFiltered(showInactive ? {} : { active: true });

    const {
        downloadInvoice,
        downloadEstimate,
        handleDownloadInvoice,
        handleShowInvoice,
        handleDownloadEstimate,
        handleShowEstimate,
        handleDeleteInvoice,
        handleCreateCreditNote,
        handleValidateInvoice,
    } = useBillingActions();

    const { handlePayInvoices, handleSendInvoicePaymentMail } = usePayActions();

    const { showMailModal } = useModalMail();

    const {
        statusFromInvoice,
        getInvoiceLabel,
        feedbackFromInvoice,
        getEstimateLabel,
        getTotalRemainingAmount,
    } = useBillingRender();

    const { canApplyTransition, applyTransition } = useTransition();
    const { modalActions } = useModal();

    const totalRemainingAmount = getTotalRemainingAmount(invoices);
    const prescriberId = dossier?.prescripteurId as number;
    const recipientId = getRecipientId() as number;
    const collections = collectionIds
        .map(getCollectionById)
        .filter((collection) => collection !== undefined);
    const dossierIds = collections.map((c) => c.dossierIds).flat();

    const roleChecker = UseKeycloakCheckRole();
    const isInterne = roleChecker(REALM_INTERN);
    const keycloakService = UseKeycloakService();
    const { prescriber, setPrescriberId } = React.useContext(DossierPrescriberContext);

    const refreshDossier = React.useCallback(() => {
        if (dispatch) {
            dispatch({ type: REFRESH_CURRENT_DOSSIER_CTX });
        }
    }, [dispatch]);

    React.useEffect(() => {
        if (prescriberId) setPrescriberId(prescriberId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prescriberId]);

    const mailContactDossier = React.useMemo(() => {
        if (!dossier?.contactId || !prescriber?.contacts) return undefined;
        const getEmail = (c: (typeof prescriber.contacts)[number]) =>
            c.emailsContact.find((e) => e.defaut)?.email;
        let contact = prescriber.contacts.find((c) => c.id === dossier.contactId);
        if (!contact || !getEmail(contact)) {
            contact = prescriber.contacts.find((c) => c.principal === true);
        }
        if (!contact) return undefined;
        const email = getEmail(contact);
        if (!email) return undefined;
        return {
            emailAddress: email,
            firstName: contact.prenomContact,
            lastName: contact.nomContact,
        };
    }, [dossier?.contactId, prescriber]);

    const handleSendEstimateSuccess = async () => {
        modalActions.close();

        const promises = (siblings || [])
            .filter((sibling) => canApplyTransition(sibling, 'send_estimate'))
            .map((sibling) => applyTransition(sibling, 'send_estimate'));

        await Promise.all(promises);

        refreshDossier();
    };

    const handleSendMail = async (
        fileName: string,
        id: number,
        downloadFn: (id: number) => Promise<Blob>,
        documentType: 'Facture' | 'Avoir' | 'Devis',
    ): Promise<void> => {
        try {
            const blob = await downloadFn(id);
            const collection = collections[0];
            const bodyContent = `Bonjour ${mailContactDossier?.firstName ?? ''} ${mailContactDossier?.lastName ?? ''}\n\n\n\nCordialement,\n${keycloakService.getName()}\n${keycloakService.getEmail()}`;

            showMailModal({
                to: mailContactDossier ? [mailContactDossier] : [],
                subject: `Legal2Digital - ${documentType} - ${collection?.numero ?? ''} - ${dossier?.numero ?? ''}`,
                showSubject: true,
                showTo: true,
                showBody: true,
                showAttachments: true,
                body: bodyContent,
                attachments: [{ file: new File([blob], fileName, { type: 'application/pdf' }) }],
                collectionIds: collections.map((c) => c.id),
                dossiersIds: dossierIds,
                searchParamsTiersIds: [prescriberId, recipientId],
                onSuccess: documentType === 'Devis' ? handleSendEstimateSuccess : undefined,
            });
        } catch {
            enqueueSnackbar("Une erreur est survenue lors de l'envoi du mail", {
                variant: 'error',
            });
        }
    };

    const handleSendMailInvoice = (fileName: string, id: number) =>
        handleSendMail(fileName, id, downloadInvoice, 'Facture');

    const handleSendMailEstimate = (fileName: string, id: number) =>
        handleSendMail(fileName, id, downloadEstimate, 'Devis');

    const onPayInvoices = () => {
        handlePayInvoices(
            dossier?.collectionNumero as string,
            totalRemainingAmount,
            invoices
                .filter((item) => item.paye === false && item.factureEnPreparation === false)
                .map((item) => item.id as number),
            'facture',
        );
    };

    const onSendInvoicePaymentMail = () => {
        try {
            handleSendInvoicePaymentMail(
                recipientId,
                prescriberId,
                collections,
                totalRemainingAmount as unknown as string,
                invoices.filter(
                    (item) => item.paye === false && item.factureEnPreparation === false,
                ),
                dossierIds,
            );
        } catch (e) {
            console.error(e);
            enqueueSnackbar("Une erreur est survenue lors de l'envoi de la demande de règlement", {
                variant: 'error',
            });
        }
    };

    const listItems = [
        ...invoices.map((invoice) => {
            let deleteInvoice;

            const hasAction =
                invoice.id !== undefined && !invoice.idFactureRefAvoir && invoice.totalTtc > 0;

            if (hasAction && invoice.factureEnPreparation) {
                deleteInvoice = () => handleDeleteInvoice(invoice, dossierIds, collectionIds);
            } else if (hasAction && !invoice.factureEnPreparation) {
                deleteInvoice = () => handleCreateCreditNote(invoice, dossierIds, collectionIds);
            }

            return {
                id: invoice.id as number,
                label: getInvoiceLabel(invoice),
                status: statusFromInvoice(invoice),
                feedback: feedbackFromInvoice(invoice),
                createdBy: invoice.createdBy,
                createdAt: invoice.createdAt,
                amount: invoice?.totalTtc as unknown as string,
                validateInvoice: invoice.factureEnPreparation
                    ? () => {
                          handleValidateInvoice(invoice, collectionIds);
                      }
                    : undefined,
                deleteInvoice,
                onDownload: handleDownloadInvoice,
                onShow: handleShowInvoice,
                onSendMail: isInterne
                    ? () =>
                          handleSendMailInvoice(
                              `${getInvoiceLabel(invoice)}.pdf`,
                              invoice.id as number,
                          )
                    : undefined,
            };
        }),
        ...estimates.map((estimate) => ({
            label: getEstimateLabel(estimate),
            status: 'paid' as InvoiceStatus,
            id: estimate.id as number,
            onDownload: handleDownloadEstimate,
            onShow: handleShowEstimate,
            onSendMail: isInterne
                ? () =>
                      handleSendMailEstimate(
                          `${getEstimateLabel(estimate)}.pdf`,
                          estimate.id as number,
                      )
                : undefined,
        })),
    ];

    return (
        <Card
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                boxShadow: 'none',
                padding: 0,
                mx: 0,
            }}
            id="devisEtFactures"
        >
            <Accordion
                sx={{
                    boxShadow: 'none',
                }}
                title="Devis et factures"
                titlesSx={{
                    fontWeight: '600',
                    boxShadow: 'none',
                }}
                headerSx={{ boxShadow: 'none' }}
                expanded
                expandColor={palette.text.primary as unknown as PaletteColor}
                expandLabel={
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: '10px',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                            }}
                        >
                            <Typography
                                variant="l2dh7"
                                sx={{ marginRight: '10px', fontSize: '0.875rem' }}
                            >
                                Cacher les devis inactifs
                            </Typography>
                            <Switch
                                value={!showInactive}
                                checked={!showInactive}
                                onClick={(e) => {
                                    e.stopPropagation();
                                }}
                                onChange={(
                                    event: React.ChangeEvent<HTMLInputElement>,
                                    checked: boolean,
                                ) => {
                                    setShowInactive(!checked);
                                }}
                            />
                        </Box>
                    </Box>
                }
                footerActions={[
                    invoices.filter((item) => item.factureEnPreparation === false).length > 0 &&
                    totalRemainingAmount > 0 ? (
                        <InvoiceProvisionPayment
                            amount={totalRemainingAmount}
                            isFacture
                            onClickDemandeReglement={onSendInvoicePaymentMail}
                            onClickPaiement={onPayInvoices}
                        />
                    ) : null,
                ]}
            >
                {listItems.length > 0 ? (
                    <InvoiceList
                        items={listItems}
                        showTimestampAndBlame
                    />
                ) : (
                    <NoResult
                        title="Aucun devis ou facture pour le moment"
                        paddingSx="15px"
                    />
                )}
            </Accordion>
        </Card>
    );
};

export default QuoteAndBillsSection;
